import { PageController } from '@client/components/_core/layout/PageController';
import { BasicTabs } from '@client/components/_core/navigation/Tabs';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { Tooltip } from '@client/components/_ui/display/Tooltip';
import { EmptyState } from '@client/components/_ui/feedback/EmptyState';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { ModalFooter } from '@client/components/_ui/feedback/ModalFooter';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useUser } from '@client/hooks/business/user/useUser';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { trpcClient, trpcOptions } from '@client/trpc/client';
import { firstQueryError, isRetryingQueries, retryFailedQueries } from '@client/trpc/query-retry';
import { getRoleDisplayName } from '@client/utils/roles/role-config';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { withRequiredRoles } from '@shared/constants/business/role-metadata';
import { supportSessionApprovalDeadline } from '@shared/constants/business/support-session';
import type { OrgRole, Role } from '@shared/constants/business/user';
import { hasPermission } from '@shared/permissions/roles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { TRPCClientError } from '@trpc/client';
import { Card, Form, Input, Modal, Tag, Typography } from 'antd';
import { Headphones } from 'lucide-react';
import { DateTime } from 'luxon';
import { useState } from 'react';

const { Text, Paragraph } = Typography;

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/support-sessions')({
    staticData: {
        breadcrumb: { title: 'Support access' },
        ui: {
            icon: Headphones,
            title: 'Support access',
            subtitle: 'Review and manage support session requests',
            menuDescription: 'Review and manage support access requests',
        },
        permissions: ['support_session:read'],
    },
    component: SupportSessionsPage,
});

// Type aliases for the API responses
type PendingSession = RouterOutputs['supportSession']['listPending'][number];
type ActiveSession = RouterOutputs['supportSession']['listActive'][number];
type HistorySession = RouterOutputs['supportSession']['listHistory']['rows'][number];

function SupportSessionsPage() {
    const queryClient = useQueryClient();
    const user = useUser();
    const canApprove = hasPermission(user.roles, 'support_session:approve');
    // A support account never decides a support request itself; the server refuses it too (CUR-2328).
    const canDecideRequests = canApprove && !user.isSupportAccount;

    // Fetch data for pending and active tabs
    const pendingQuery = useQuery(trpcOptions.supportSession.listPending.queryOptions());
    const activeQuery = useQuery(trpcOptions.supportSession.listActive.queryOptions());
    const { data: pendingData, isLoading: isLoadingPending } = pendingQuery;
    const { data: activeData, isLoading: isLoadingActive } = activeQuery;
    const sessionQueries = [pendingQuery, activeQuery];
    const queryError = firstQueryError(sessionQueries);

    const pendingCount = pendingData?.length ?? 0;

    return (
        <PageController route={Route}>
            {queryError ? (
                <InlineQueryError
                    error={queryError}
                    onRetry={() => retryFailedQueries(sessionQueries)}
                    retrying={isRetryingQueries(sessionQueries)}
                />
            ) : (
                <>
                    <BasicTabs
                        urlKey="tab"
                        defaultActiveKey="pending"
                        items={[
                            {
                                key: 'pending',
                                label: (
                                    <span>
                                        Pending Requests
                                        {pendingCount > 0 && (
                                            <Tag color="blue" className="ml-2">
                                                {pendingCount}
                                            </Tag>
                                        )}
                                    </span>
                                ),
                                children: (
                                    <PendingRequestsTab
                                        sessions={pendingData ?? []}
                                        isLoading={isLoadingPending}
                                        canApprove={canDecideRequests}
                                        onAction={() => {
                                            void queryClient.invalidateQueries(
                                                trpcOptions.supportSession.listPending.pathFilter(),
                                            );
                                            // The Support access navigation badge (CUR-2328).
                                            void queryClient.invalidateQueries(
                                                trpcOptions.supportSession.countPending.pathFilter(),
                                            );
                                            void queryClient.invalidateQueries(
                                                trpcOptions.supportSession.listActive.pathFilter(),
                                            );
                                            void queryClient.invalidateQueries(
                                                trpcOptions.supportSession.listHistory.pathFilter(),
                                            );
                                        }}
                                    />
                                ),
                            },
                            {
                                key: 'active',
                                label: 'Active Sessions',
                                children: (
                                    <ActiveSessionsTab
                                        sessions={activeData ?? []}
                                        isLoading={isLoadingActive}
                                        canApprove={canApprove}
                                        onAction={() => {
                                            void queryClient.invalidateQueries(
                                                trpcOptions.supportSession.listActive.pathFilter(),
                                            );
                                            void queryClient.invalidateQueries(
                                                trpcOptions.supportSession.listHistory.pathFilter(),
                                            );
                                        }}
                                    />
                                ),
                            },
                            {
                                key: 'history',
                                label: 'History',
                                children: <HistoryTab />,
                            },
                        ]}
                    />
                </>
            )}
        </PageController>
    );
}

/**
 * A refusal that means the cached request is stale (CUR-2328): it lapsed, another approver or the
 * expiry sweep decided it first, or it is gone. The lists are refreshed, so the card leaves once the
 * request is no longer pending; a network or server failure keeps the card and the entered note.
 */
const isStaleRequestRefusal = (error: unknown) =>
    error instanceof TRPCClientError && (error.data?.code === 'BAD_REQUEST' || error.data?.code === 'NOT_FOUND');

function getDisplayName(user: { firstName: string | null; lastName: string | null; email: string }) {
    return user.firstName || user.lastName ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : null;
}

interface PendingRequestsTabProps {
    sessions: PendingSession[];
    isLoading: boolean;
    canApprove: boolean;
    onAction: () => void;
}

function PendingRequestsTab({ sessions, isLoading, canApprove, onAction }: PendingRequestsTabProps) {
    const { formatDateTime } = useFormatter();
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);
    const [selectedSession, setSelectedSession] = useState<PendingSession | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = Form.useForm();

    const handleOpenReviewModal = (session: PendingSession, action: 'approve' | 'reject') => {
        setSelectedSession(session);
        setReviewAction(action);
        setReviewModalOpen(true);
        form.resetFields();
    };

    const handleSubmitReview = async (values: { reviewNote?: string }) => {
        if (!selectedSession || !reviewAction) return;

        setIsSubmitting(true);
        try {
            if (reviewAction === 'approve') {
                await trpcClient.supportSession.approve.mutate({
                    id: selectedSession.id,
                    reviewNote: values.reviewNote,
                });
                message.success('Support session approved successfully');
            } else {
                await trpcClient.supportSession.reject.mutate({
                    id: selectedSession.id,
                    reviewNote: values.reviewNote,
                });
                message.success('Support session rejected');
            }
            setReviewModalOpen(false);
            onAction();
        } catch (err) {
            errorMessage.show(err);
            if (isStaleRequestRefusal(err)) onAction();
        } finally {
            setIsSubmitting(false);
        }
    };

    // The review closes once its request leaves the pending list (decided, lapsed or gone).
    const reviewOpen =
        reviewModalOpen && selectedSession !== null && sessions.some((session) => session.id === selectedSession.id);

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <Loading size="large" />
            </div>
        );
    }

    if (sessions.length === 0) {
        return <EmptyState icon={<Headphones />} title="No pending support session requests" />;
    }

    return (
        <>
            <div className="space-y-4">
                {sessions.map((session) => {
                    const supportUserName = getDisplayName(session.SupportUser);
                    const requestedByName = getDisplayName(session.RequestedByUser);

                    return (
                        <Card key={String(session.id)} size="small">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="mb-3 flex items-center gap-2">
                                        {supportUserName ? (
                                            <>
                                                <Text strong className="text-base">
                                                    {supportUserName}
                                                </Text>
                                                <Text type="secondary">{session.SupportUser.email}</Text>
                                            </>
                                        ) : (
                                            <Text strong className="text-base">
                                                {session.SupportUser.email}
                                            </Text>
                                        )}
                                        <StatusBadge module="supportSession" status="pending" customLabel="Pending" />
                                    </div>

                                    <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                        <div>
                                            <Text type="secondary">Requested Roles: </Text>
                                            <span className="inline-flex flex-wrap gap-1">
                                                {/* Show what approval will actually grant — legacy requests
                                                    may predate request-time pairing (CUR-851). */}
                                                {withRequiredRoles(session.requestedRoles as OrgRole[]).map((role) => (
                                                    <Tag key={role} variant="outlined" color="blue">
                                                        {getRoleDisplayName(role)}
                                                    </Tag>
                                                ))}
                                            </span>
                                        </div>
                                        <div>
                                            <Text type="secondary">Expires: </Text>
                                            <Text>{formatDateTime(session.expiresAt)}</Text>
                                        </div>
                                        <div>
                                            <Text type="secondary">Requested By: </Text>
                                            <Tooltip title={session.RequestedByUser.email}>
                                                <Text>{requestedByName || session.RequestedByUser.email}</Text>
                                            </Tooltip>
                                        </div>
                                        <div>
                                            <Text type="secondary">Requested At: </Text>
                                            <Text>{formatDateTime(session.createdAt)}</Text>
                                        </div>
                                        <div>
                                            {/* After this the request lapses and can no longer be approved (CUR-2328). */}
                                            <Text type="secondary">Respond by: </Text>
                                            <Text>{formatDateTime(supportSessionApprovalDeadline(session))}</Text>
                                        </div>
                                    </div>

                                    {session.requestNote && (
                                        <div className="mt-3 rounded-md bg-muted p-3">
                                            <Text type="secondary" className="mb-1 block text-xs">
                                                Request Note:
                                            </Text>
                                            <Paragraph className="mb-0 whitespace-pre-wrap text-sm">
                                                {session.requestNote}
                                            </Paragraph>
                                        </div>
                                    )}
                                </div>

                                {canApprove && (
                                    <Space className="ml-4">
                                        <Button
                                            type="primary"
                                            onClick={() => handleOpenReviewModal(session, 'approve')}
                                        >
                                            Approve
                                        </Button>
                                        <Button type="danger" onClick={() => handleOpenReviewModal(session, 'reject')}>
                                            Reject
                                        </Button>
                                    </Space>
                                )}
                            </div>
                        </Card>
                    );
                })}
            </div>

            <Modal
                title={reviewAction === 'approve' ? 'Approve Support Session' : 'Reject Support Session'}
                open={reviewOpen}
                onCancel={() => setReviewModalOpen(false)}
                footer={
                    <ModalFooter
                        formId="support-session-review-form"
                        onCancel={() => setReviewModalOpen(false)}
                        okText={reviewAction === 'approve' ? 'Approve' : 'Reject'}
                        okDanger={reviewAction === 'reject'}
                        okLoading={isSubmitting}
                    />
                }
                destroyOnHidden
            >
                <Form form={form} id="support-session-review-form" layout="vertical" onFinish={handleSubmitReview}>
                    {selectedSession && (
                        <div className="mb-4 rounded-md bg-muted p-3">
                            <Text strong>
                                {getDisplayName(selectedSession.SupportUser) || selectedSession.SupportUser.email}
                            </Text>
                            <br />
                            <Text type="secondary">{selectedSession.SupportUser.email}</Text>
                        </div>
                    )}

                    <Form.Item
                        name="reviewNote"
                        label={reviewAction === 'approve' ? 'Note (optional)' : 'Reason for rejection (optional)'}
                    >
                        <Input.TextArea
                            placeholder={
                                reviewAction === 'approve'
                                    ? 'Add an optional note...'
                                    : 'Provide a reason for rejecting this request...'
                            }
                            rows={3}
                            maxLength={1000}
                            showCount
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

interface ActiveSessionsTabProps {
    sessions: ActiveSession[];
    isLoading: boolean;
    canApprove: boolean;
    onAction: () => void;
}

function ActiveSessionsTab({ sessions, isLoading, canApprove, onAction }: ActiveSessionsTabProps) {
    const { formatDateTime } = useFormatter();
    const [revokeModalOpen, setRevokeModalOpen] = useState(false);
    const [selectedSession, setSelectedSession] = useState<ActiveSession | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = Form.useForm();

    const handleOpenRevokeModal = (session: ActiveSession) => {
        setSelectedSession(session);
        setRevokeModalOpen(true);
        form.resetFields();
    };

    const handleSubmitRevoke = async (values: { revokeReason?: string }) => {
        if (!selectedSession) return;

        setIsSubmitting(true);
        try {
            await trpcClient.supportSession.revoke.mutate({
                id: selectedSession.id,
                revokeReason: values.revokeReason,
            });
            message.success('Support session revoked successfully');
            setRevokeModalOpen(false);
            onAction();
        } catch (err) {
            errorMessage.show(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <Loading size="large" />
            </div>
        );
    }

    if (sessions.length === 0) {
        return <EmptyState icon={<Headphones />} title="No active support sessions" />;
    }

    return (
        <>
            <div className="space-y-4">
                {sessions.map((session) => {
                    const isExpiringSoon = DateTime.fromJSDate(session.expiresAt).diffNow('days').days < 1;
                    const supportUserName = getDisplayName(session.SupportUser);
                    const reviewerName = session.ReviewedByUser?.User
                        ? getDisplayName(session.ReviewedByUser.User)
                        : null;
                    const isAutoApproved = !session.ReviewedByUser?.User && session.reviewedAt;

                    return (
                        <Card key={String(session.id)} size="small">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="mb-3 flex items-center gap-2">
                                        {supportUserName ? (
                                            <>
                                                <Text strong className="text-base">
                                                    {supportUserName}
                                                </Text>
                                                <Text type="secondary">{session.SupportUser.email}</Text>
                                            </>
                                        ) : (
                                            <Text strong className="text-base">
                                                {session.SupportUser.email}
                                            </Text>
                                        )}
                                        <StatusBadge module="supportSession" status="approved" />
                                    </div>

                                    <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                        <div>
                                            <Text type="secondary">Roles: </Text>
                                            <span className="inline-flex flex-wrap gap-1">
                                                {/* Show what the membership actually holds — approval
                                                    appends mandatory companion roles (CUR-846). */}
                                                {withRequiredRoles(session.requestedRoles as OrgRole[]).map((role) => (
                                                    <Tag key={role} variant="outlined" color="blue">
                                                        {getRoleDisplayName(role)}
                                                    </Tag>
                                                ))}
                                            </span>
                                        </div>
                                        <div>
                                            <Text type="secondary">Expires: </Text>
                                            <span className={isExpiringSoon ? 'text-orange-500' : ''}>
                                                {formatDateTime(session.expiresAt)}
                                                {isExpiringSoon && (
                                                    <Tooltip title="Expires within 24 hours">
                                                        <Tag color="orange" className="ml-2">
                                                            Soon
                                                        </Tag>
                                                    </Tooltip>
                                                )}
                                            </span>
                                        </div>
                                        <div>
                                            <Text type="secondary">Approved By: </Text>
                                            {isAutoApproved ? (
                                                <Tooltip title="Automatically approved during organisation setup">
                                                    <Text italic>System (Auto-approved)</Text>
                                                </Tooltip>
                                            ) : reviewerName ? (
                                                <Text>{reviewerName}</Text>
                                            ) : (
                                                <Text>-</Text>
                                            )}
                                        </div>
                                        <div>
                                            <Text type="secondary">Approved At: </Text>
                                            <Text>{session.reviewedAt ? formatDateTime(session.reviewedAt) : '-'}</Text>
                                        </div>
                                    </div>
                                </div>

                                {canApprove && (
                                    <Button
                                        type="danger"
                                        className="ml-4"
                                        onClick={() => handleOpenRevokeModal(session)}
                                    >
                                        Revoke Access
                                    </Button>
                                )}
                            </div>
                        </Card>
                    );
                })}
            </div>

            <Modal
                title="Revoke Support Session"
                open={revokeModalOpen}
                onCancel={() => setRevokeModalOpen(false)}
                footer={
                    <ModalFooter
                        formId="support-session-revoke-form"
                        onCancel={() => setRevokeModalOpen(false)}
                        okText="Revoke Access"
                        okDanger
                        okLoading={isSubmitting}
                    />
                }
                destroyOnHidden
            >
                <Form form={form} id="support-session-revoke-form" layout="vertical" onFinish={handleSubmitRevoke}>
                    {selectedSession && (
                        <div className="mb-4 rounded-md bg-muted p-3">
                            <Text strong>
                                {getDisplayName(selectedSession.SupportUser) || selectedSession.SupportUser.email}
                            </Text>
                            <br />
                            <Text type="secondary">{selectedSession.SupportUser.email}</Text>
                        </div>
                    )}

                    <div className="mb-4 rounded-md border border-orange-200 bg-orange-50 p-3">
                        <Text type="secondary" className="text-xs">
                            <strong>Warning:</strong> Revoking this session will immediately remove the support
                            account's access to your organisation. This action cannot be undone.
                        </Text>
                    </div>

                    <Form.Item name="revokeReason" label="Reason for revocation (optional)">
                        <Input.TextArea
                            placeholder="Provide a reason for revoking this session..."
                            rows={3}
                            maxLength={1000}
                            showCount
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

type HistoryQueryVariables = RouterInputs['supportSession']['listHistory'];
type HistoryOrderBy = NonNullable<HistoryQueryVariables['orderBy']>;

const historyColumnHelper = createColumnHelper<HistorySession, HistoryOrderBy>();

function HistoryTab() {
    const { formatDateTime } = useFormatter();

    const [queryVariables, setQueryVariables] = usePathState<HistoryQueryVariables>('historyQuery', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { createdAt: 'desc' },
    });

    const { data, refetch, isFetching, error } = useQuery(
        trpcOptions.supportSession.listHistory.queryOptions(queryVariables),
    );

    const columns = [
        historyColumnHelper.accessor('SupportUser', {
            id: 'supportUser',
            header: 'Support Account',
            cell: ({ value }) => {
                const name = getDisplayName(value);
                return name ? (
                    <div>
                        <Text strong>{name}</Text>
                        <br />
                        <Text type="secondary" className="text-xs">
                            {value.email}
                        </Text>
                    </div>
                ) : (
                    <Text strong>{value.email}</Text>
                );
            },
        }),
        historyColumnHelper.accessor('requestedRoles', {
            id: 'roles',
            header: 'Roles',
            cell: ({ value }) => (
                <div className="flex flex-wrap gap-1">
                    {(value as Role[]).slice(0, 2).map((role) => (
                        <Tag key={role} variant="outlined" color="blue">
                            {getRoleDisplayName(role)}
                        </Tag>
                    ))}
                    {(value as Role[]).length > 2 && (
                        <Tooltip title={(value as Role[]).slice(2).map(getRoleDisplayName).join(', ')}>
                            <Tag variant="outlined">+{(value as Role[]).length - 2}</Tag>
                        </Tooltip>
                    )}
                </div>
            ),
        }),
        historyColumnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            sortable: 'status',
            cell: ({ value }) => <StatusBadge module="supportSession" status={value as string} />,
            width: 120,
        }),
        historyColumnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Created',
            sortable: 'createdAt',
            cell: ({ value }) => formatDateTime(value),
        }),
        historyColumnHelper.accessor('expiresAt', {
            id: 'expiresAt',
            header: 'Expires',
            sortable: 'expiresAt',
            cell: ({ value }) => formatDateTime(value),
        }),
        historyColumnHelper.accessor('RequestedByUser', {
            id: 'requestedBy',
            header: 'Requested By',
            cell: ({ value }) => {
                const name = getDisplayName(value);
                return <Tooltip title={value.email}>{name || value.email}</Tooltip>;
            },
        }),
    ] as const;

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isFetching,
        columns,
        rowKey: 'id',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'org-support-sessions-history-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        emptyState: { noRecordsTitle: 'No support session history yet' },
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />;
    }

    return <PrismaTable {...tableProps} />;
}
